import isEmpty from 'lodash/isEmpty';
import { getPropertyMap } from 'modules/journey/components/FilterInputsList';
import {
  comparatorIcons,
  defaultComparators,
} from 'modules/journey/components/FilterInputsList/FilterInputsList.constants';
import { Query } from 'modules/journey/components/FilterInputsList/FilterInputsList.types';
import { ContainsCloningError } from 'modules/journey/components/RuleBuilder/types';
import { useFieldMappingsContext } from 'modules/workflows/context/FieldMappingsContext';
import {
  fetchEventFieldMappingsRequest,
  getEventFieldMappings,
  getIsJourneyFieldMappingsEnabled,
} from 'modules/workflows/ducks';
import { getESFieldDisplayName } from 'modules/workflows/utils';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IS_NOT_SET, IS_SET } from 'utils/comparatorTypes';
import {
  CAMPAIGN,
  LIST_MEMBERSHIP,
  MESSAGE_CHANNEL,
  MESSAGE_TYPE,
  UNSUBSCRIBED_CHANNELS,
  UNSUBSCRIBED_MESSAGE_TYPES,
  USER_LIST_MEMBERSHIP,
} from 'utils/fieldConstants';

type AdditionalQueryProperties = {
  valueDisplay?: string;
  fieldDisplay?: string;
  comparatorTypeDisplay?: string;
};

type InitialSearchQuery = Partial<Query> & {
  field?: string | null;
};

export type SearchQuery = InitialSearchQuery & AdditionalQueryProperties;

/**
  The following types are gathered from FilterValueInput

  todo: when campaign cloning is implemented, we will need to
  remove CAMPAIGN from this list if the user chooses to clone
  a campaign
*/
const FILTER_SELECT_TYPES = [
  LIST_MEMBERSHIP,
  CAMPAIGN,
  MESSAGE_CHANNEL,
  MESSAGE_TYPE,
  USER_LIST_MEMBERSHIP,
  UNSUBSCRIBED_CHANNELS,
  UNSUBSCRIBED_MESSAGE_TYPES,
];

export const generateErrorObject = (value = '') => {
  return { validationError: true, displayValue: value };
};

/**
 * Returns a search query object to display in FiltersInput
 */
export const validateSearchQuery = (searchQuery: SearchQuery & ContainsCloningError): any => {
  const {
    field,
    value,
    comparatorType,
    id,
    dataType,
    valueDisplay,
    fieldDisplay,
    comparatorTypeDisplay = '',
    containsCloningError = false,
  } = searchQuery;

  // if the ID property exists, the field is new
  if (id && !containsCloningError) return {};

  const hasNullValue = value === null;
  const hasSetComparator = comparatorType === IS_SET || comparatorType === IS_NOT_SET;

  // makes strings "display-able" and match existing formatting
  const parsedFieldDisplay = getESFieldDisplayName(fieldDisplay, dataType);
  const comparatorString = defaultComparators.find((item) => item.type === comparatorTypeDisplay);
  const parsedComparatorDisplay =
    comparatorIcons[comparatorTypeDisplay] || comparatorString?.displayName?.toLowerCase() || '';

  const searchQueryErrorObject = {
    ...(!hasSetComparator && hasNullValue && { value: generateErrorObject(valueDisplay) }),
    // if the field doesn't exist, the user needs to reselect a comparator
    ...(!field && {
      field: generateErrorObject(parsedFieldDisplay),
      comparatorType: generateErrorObject(parsedComparatorDisplay),
    }),
  };
  return isEmpty(searchQueryErrorObject) ? {} : searchQueryErrorObject;
};

const reduceSearchQueryErrors = (acc: any, searchQuery: SearchQuery, idx: number) => {
  const searchQueryValidations = validateSearchQuery(searchQuery);
  if (!isEmpty(searchQueryValidations)) {
    acc[idx] = searchQueryValidations;
  }
  return acc;
};

/**
 * Parse initial queries to drop values to display to the
 * user to help the user choose a new selection
 */
export const parseQueries = (clonedQueries: InitialSearchQuery[], propertyMap: any = {}) =>
  clonedQueries?.map((query: InitialSearchQuery) => {
    const { field = '', value, comparatorType } = query;
    const fieldExists = propertyMap?.[field];

    return {
      ...query,
      // strip values that can't be copied from the clone
      field: fieldExists ? field : null,
      comparatorType: fieldExists ? comparatorType : null,
      value: !FILTER_SELECT_TYPES.includes(field) ? value : null,
      // values to display as placeholders
      fieldDisplay: field,
      comparatorTypeDisplay: comparatorType,
      valueDisplay: value,
    };
  }) as SearchQuery[];

/**
 * Hook that does a few things:
 *  1. Checks if the search filters were cloned
 *  2. Sets the current search filters with the cloned filters
 *  3. Returns a validation error object
 * @returns {object} searchQueriesErrors
 */
export const useClonedQueryErrors = ({
  clonedFromSearchCombo,
  initialSearchCombo,
  searchQueries = [],
  updateSearchCombo,
  dataType,
}: {
  clonedFromSearchCombo: any;
  initialSearchCombo: any;
  searchQueries?: SearchQuery[];
  updateSearchCombo: (data: any) => void;
  dataType: string;
}) => {
  const isJourneyFieldMappingsEnabled = useSelector(getIsJourneyFieldMappingsEnabled);
  const dispatch = useDispatch();
  const { fieldMappings } = useFieldMappingsContext();
  const mappingsOld = useSelector(getEventFieldMappings) || {};
  const mappings = isJourneyFieldMappingsEnabled ? fieldMappings : mappingsOld;

  // todo: need better way to determine if searchCombo is cloned
  // otherwise this will still run if search combo is cleared after being cloned
  const isCloned = clonedFromSearchCombo && !initialSearchCombo;

  useEffect(() => {
    if (!isJourneyFieldMappingsEnabled) dispatch(fetchEventFieldMappingsRequest());
  }, []);

  useEffect(() => {
    if (isCloned) {
      const { searchQueries: clonedQueries, combinator } = clonedFromSearchCombo;

      // todo: update based on findings from spike WF-4260
      const propertyMap = getPropertyMap({ mappings, dataType, isUUIDProject: false });
      const parsedInitialQueries = parseQueries(clonedQueries, propertyMap);

      updateSearchCombo({
        searchQueries: parsedInitialQueries,
        combinator,
      });
    }
  }, [clonedFromSearchCombo, initialSearchCombo, mappings]);

  if (!isCloned) return {};

  const searchQueriesErrors = searchQueries?.reduce(reduceSearchQueryErrors, {}) || {};
  return searchQueriesErrors;
};
